import http from '@/api/http'
import { appDateString, backendSlotDayShift, slotFromBackend, slotToBackend } from '@/utils/appTime'
import { groupsPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export type GroupScheduleRecurrenceType = 'WEEKLY' | 'MONTHLY_BY_DAY' | 'MONTHLY_BY_WEEKDAY'

export interface GroupSchedule {
  // One recurring slot from a group's `schedule` list.

  id: string
  // Schedule slot id (UUID7).

  recurrence_type: GroupScheduleRecurrenceType
  // How this slot recurs. The create/edit UI only ever produces WEEKLY
  // slots (the only kind the design sketches show); MONTHLY_* slots can
  // still arrive from elsewhere and are rendered, just not editable here.

  day_of_week: number | null
  // ISO day of week (1=Monday..7=Sunday); set for WEEKLY and MONTHLY_BY_WEEKDAY.

  day_of_month: number | null
  // Day of the month; set for MONTHLY_BY_DAY.

  week_of_month: number | null
  // 1-indexed occurrence of day_of_week within the month; set for MONTHLY_BY_WEEKDAY.

  start_time: string
  // "HH:MM:SS", local wall-clock time.

  end_time: string
  // "HH:MM:SS", local wall-clock time.
}

export interface Group {
  // One row from GET `/groups` (cursor-paginated list) or GET `/groups/{id}`.

  id: string
  // Group id (UUID7).

  name: string | null
  // Display name; nullable in the backend (falls back to description in the UI).

  description: string | null
  // Optional human-readable description.

  start: string
  // "YYYY-MM-DD" — the date the group started.

  end: string | null
  // "YYYY-MM-DD" — the date the group ended/will end; null while still active.

  has_dedicated_schedule: boolean
  // Whether this group's `schedule` also auto-generates real bookings
  // against `booking_type_id` (via the backend's monthly rolling-window
  // cron job) — immutable after creation.

  booking_type_id: string | null
  // The booking type (room) the dedicated schedule books against, when
  // `has_dedicated_schedule` is true. Resolve its `address` via the
  // booking-types catalog (`useAllBookingTypes`) for display as "room".

  schedule: GroupSchedule[] | null
  // Recurring weekly template, independent of `has_dedicated_schedule` —
  // present on GET `/groups/{id}`; may be absent (not just empty) on the
  // list endpoint depending on backend version, so callers should treat
  // `null`/`undefined` the same as "unknown, fetch the detail".

  performer_ids: string[]
  // Distinct performer ids across the group's linked business services
  // (a separate, older link — resolve names/gender via `fetchPerformers`).

  business_service_type_id: string | null
  // Business service type the group is for; `null` when not set.
}

interface GroupsResponse extends PageMeta {
  // Paginated list payload from GET `/groups`.

  items: Group[]
  // Page of groups.
}

export interface CreateGroupSchedulePayload {
  // One slot inside `CreateGroupPayload.schedule`, or the body of POST
  // `/groups/{id}/schedule`. Only WEEKLY is produced by this app's UI.

  recurrence_type?: GroupScheduleRecurrenceType
  day_of_week?: number | null
  day_of_month?: number | null
  week_of_month?: number | null
  start_time: string
  end_time: string
}

export interface UpdateGroupSchedulePayload {
  // Request body for PATCH `/groups/{group_id}/schedule/{schedule_id}`.

  recurrence_type?: GroupScheduleRecurrenceType
  day_of_week?: number | null
  day_of_month?: number | null
  week_of_month?: number | null
  start_time?: string
  end_time?: string
}

export interface CreateGroupPayload {
  // Request body for POST `/groups`.

  name?: string | null
  description?: string | null
  start: string
  has_dedicated_schedule?: boolean
  booking_type_id?: string | null
  // `schedule` is required by the backend when `has_dedicated_schedule` is
  // true, and is otherwise silently discarded at creation time — add slots
  // afterward via `createGroupSchedule` instead (works regardless of
  // `has_dedicated_schedule`).
  schedule?: CreateGroupSchedulePayload[] | null

  performer_ids?: string[]
  // Performers assigned to the group; omitted/empty means none.

  business_service_type_id?: string | null
  // Service type ("вид занятий") the group is assigned to.
}

export interface UpdateGroupPayload {
  // Request body for PATCH `/groups/{id}`. `has_dedicated_schedule` and
  // `booking_type_id` are not editable after creation.

  name?: string | null
  description?: string | null
  start?: string
  end?: string | null

  business_service_type_id?: string | null
  // Service type to assign; null clears it, omit to leave unchanged.

  performer_ids?: string[]
  // Full replacement list of assigned performers; omit to leave unchanged.
}

/**
 * Slots are converted UTC → UTC+3 on the way in, whatever their recurrence, so
 * every slot reads in the same zone. A shift past midnight also moves the
 * weekday (WEEKLY, MONTHLY_BY_WEEKDAY) or the day of month (MONTHLY_BY_DAY,
 * wrapping 31 → 1); `week_of_month` is left as is, so a monthly slot that
 * crosses midnight on a month boundary is approximate. The UI only creates
 * WEEKLY slots.
 */
function slotFromApi(slot: GroupSchedule): GroupSchedule {
  if (slot.recurrence_type === 'MONTHLY_BY_DAY') {
    if (slot.day_of_month == null) return slot
    const shifted = slotFromBackend({ ...slot, day_of_week: 1 })
    const day = slot.day_of_month + backendSlotDayShift(slot.start_time)
    return {
      ...slot,
      day_of_month: day > 31 ? 1 : day,
      start_time: shifted.start_time,
      end_time: shifted.end_time,
    }
  }
  if (slot.day_of_week == null) return slot
  return { ...slot, ...slotFromBackend({ ...slot, day_of_week: slot.day_of_week }) }
}

function groupFromApi(group: Group): Group {
  return group.schedule ? { ...group, schedule: group.schedule.map(slotFromApi) } : group
}

/** Weekly slots are converted UTC+3 → UTC on the way out; a partial update that lacks day/start/end is sent as-is. */
function slotToApi<T extends CreateGroupSchedulePayload | UpdateGroupSchedulePayload>(slot: T): T {
  const { day_of_week, start_time, end_time } = slot
  if (
    (slot.recurrence_type ?? 'WEEKLY') !== 'WEEKLY' ||
    day_of_week == null ||
    start_time === undefined ||
    end_time === undefined
  ) {
    return slot
  }
  return { ...slot, ...slotToBackend({ day_of_week, start_time, end_time }) }
}

/**
 * Fetches a page of groups.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchGroups(cursor?: number): Promise<GroupsResponse> {
  const { data } = await http.get<GroupsResponse>('/groups', {
    params: cursorParams(groupsPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchGroups] GET /groups response', data)
  }
  return { ...data, items: (data.items ?? []).map(groupFromApi) }
}

/**
 * Fetches a single group by id, with its schedule.
 */
export async function fetchGroup(id: string): Promise<Group> {
  const { data } = await http.get<Group>(`/groups/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchGroup] GET /groups/${id} response`, data)
  }
  return groupFromApi(data)
}

/**
 * Creates a new group via POST `/groups`.
 */
export async function createGroup(payload: CreateGroupPayload): Promise<Group> {
  const body = payload.schedule
    ? { ...payload, schedule: payload.schedule.map(slotToApi) }
    : payload
  const { data } = await http.post<Group>('/groups', body)
  if (import.meta.env.DEV) {
    console.log('[createGroup] POST /groups response', data)
  }
  return groupFromApi(data)
}

/**
 * Updates a group via PATCH `/groups/{id}`.
 */
export async function updateGroup(id: string, payload: UpdateGroupPayload): Promise<Group> {
  const { data } = await http.patch<Group>(`/groups/${id}`, payload)
  if (import.meta.env.DEV) {
    console.log(`[updateGroup] PATCH /groups/${id} response`, data)
  }
  return groupFromApi(data)
}

/**
 * Deletes a group by id. Rejected by the backend (409) if the group still
 * has past booking history; upcoming dedicated-schedule bookings are
 * cleaned up as part of the delete.
 */
export async function deleteGroup(id: string): Promise<void> {
  await http.delete(`/groups/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteGroup] DELETE /groups/${id}`)
  }
}

/**
 * Adds a schedule slot to a group via POST `/groups/{groupId}/schedule`.
 */
export async function createGroupSchedule(
  groupId: string,
  payload: CreateGroupSchedulePayload,
): Promise<GroupSchedule> {
  const { data } = await http.post<GroupSchedule>(`/groups/${groupId}/schedule`, slotToApi(payload))
  if (import.meta.env.DEV) {
    console.log(`[createGroupSchedule] POST /groups/${groupId}/schedule response`, data)
  }
  return slotFromApi(data)
}

/**
 * Updates a group's schedule slot via PATCH `/groups/{groupId}/schedule/{scheduleId}`.
 */
export async function updateGroupSchedule(
  groupId: string,
  scheduleId: string,
  payload: UpdateGroupSchedulePayload,
): Promise<GroupSchedule> {
  const { data } = await http.patch<GroupSchedule>(
    `/groups/${groupId}/schedule/${scheduleId}`,
    slotToApi(payload),
  )
  if (import.meta.env.DEV) {
    console.log(
      `[updateGroupSchedule] PATCH /groups/${groupId}/schedule/${scheduleId} response`,
      data,
    )
  }
  return slotFromApi(data)
}

/**
 * Removes a schedule slot from a group via DELETE `/groups/{groupId}/schedule/{scheduleId}`.
 */
export async function deleteGroupSchedule(groupId: string, scheduleId: string): Promise<void> {
  await http.delete(`/groups/${groupId}/schedule/${scheduleId}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteGroupSchedule] DELETE /groups/${groupId}/schedule/${scheduleId}`)
  }
}

/**
 * True when a group is still active — no end date, or its end date hasn't
 * passed yet. Mirrors the sketch's "активна"/"отключена" state, which the
 * backend derives the same way (`get_active_dedicated_schedule_groups`)
 * rather than storing as its own column.
 */
export function isGroupActive(group: Pick<Group, 'end'>): boolean {
  if (group.end == null) return true
  if (Number.isNaN(new Date(`${group.end}T00:00:00Z`).getTime())) return true
  // Both sides are plain "YYYY-MM-DD" strings (today taken in the app's
  // UTC+3 zone), which compare correctly as text.
  return group.end >= appDateString(new Date())
}
